import { IItemsInputJobAppointeeGrouping } from '@/api/types/request.types'
import { DefaultFilterIDs, StatusTypeValues, DueTypeValues } from '@/types'
import { INavMenuItem } from '@/types/data-objects/global'
import SortAssistant from '@/helpers/sort-assistant'
import {
  IItemTraitSet,
  DefaultItemTraitSet,
} from '@/types/data-objects/listing'
import { cloneDeep } from 'lodash'

export default new class ItemFilterAssistant {
  private get defaultFilterIdentifierPrefix(): string {
    return 'default-'
  }

  private get customFilterIdentifierPrefix(): string {
    return 'custom-'
  }

  /* Public methods */
  public get DefaultInitialFilterID(): number {
    return DefaultFilterIDs.ACTIVE
  }
  public get DefaultInitialFilterIdentifier(): string {
    return this.getDefaultFilterIdentifier(this.DefaultInitialFilterID)
  }

  public getUnnamedFilterIdentifier(): string {
    return 'unnamed-filter'
  }

  public getDefaultFilterIdentifier(val: string | number): string {
    return `${this.defaultFilterIdentifierPrefix}${val}`
  }

  public getCustomFilterIdentifier(val: string | number): string {
    return `${this.customFilterIdentifierPrefix}${val}`
  }

  public isDefaultIdentifier(val: string) {
    return val && val.startsWith(this.defaultFilterIdentifierPrefix)
  }

  public isCustomIdentifier(val: string) {
    return val && val.startsWith(this.customFilterIdentifierPrefix)
  }

  public getIDOfFilterIdentifier(val: string): number {
    const result = val
      .replace(this.defaultFilterIdentifierPrefix, '')
      .replace(this.customFilterIdentifierPrefix, '')

    return parseInt(result, 10)
  }

  public getEmptyFilterNavItem(): INavMenuItem {
    return {
      count: 0,
      icon: '',
      label: '',
      value: this.getUnnamedFilterIdentifier(),
    }
  }

  public isDueNowFilterIdentifier(val: string) {
    return val === this.getDefaultFilterIdentifier(DefaultFilterIDs.DUE_NOW)
  }

  public isSpamFilterIdentifier(val: string) {
    return val === this.getDefaultFilterIdentifier(DefaultFilterIDs.SPAM)
  }

  public convertJobAppointeeGroupingsToStringRepresentation(
    jobAppointeeGroupings: IItemsInputJobAppointeeGrouping[],
  ): string {
    let result = ''
    if (!jobAppointeeGroupings) {
      return result
    }
    let first = true
    jobAppointeeGroupings.forEach(m => {
      if (m.listAppointeeIDs.length === 0) {
        return
      }

      if (first) {
        first = false
      } else {
        result += '|'
      }
      result +=
        m.jobName.toLowerCase().trim() +
        ':' +
        SortAssistant.sortChronologically(m.listAppointeeIDs).join('!')
    })
    return result
  }

  public convertStringToJobAppointeeGroupingsRepresentation(
    stringRep: string,
  ): IItemsInputJobAppointeeGrouping[] {
    if (stringRep === null || stringRep.trim() === '') {
      return []
    }
    const jobAppointeeGroupingString = stringRep.split('|')
    const result = [] as IItemsInputJobAppointeeGrouping[]

    jobAppointeeGroupingString.forEach(jags => {
      const splitting = jags.split(':')
      const jobName = splitting[0]
      const appointeesString = splitting[1]
      const grouping = {} as IItemsInputJobAppointeeGrouping

      grouping.jobName = jobName
      grouping.listAppointeeIDs = appointeesString
        .split('!')
        .map(j => parseFloat(j))

      result.push(grouping)
    })
    return result
  }

  /* Default filter trait sets */
  public getItemFilterTraitSet_Active(): IItemTraitSet {
    const result = cloneDeep(DefaultItemTraitSet)
    result.statusType = StatusTypeValues.ACTIVE
    return result
  }
  public getItemTraitSet_AssignedToOthers(): IItemTraitSet {
    const result = cloneDeep(DefaultItemTraitSet)
    result.assignedNotToYou = true
    result.statusType = StatusTypeValues.ACTIVE
    return result
  }
  public getItemTraitSet_AssignedToYou(loggedInUserID: number): IItemTraitSet {
    const result = cloneDeep(DefaultItemTraitSet)
    result.assigneeIDs.push(loggedInUserID)
    result.statusType = StatusTypeValues.ACTIVE
    return result
  }
  public getItemTraitSet_CreatedByYou(loggedInUserID: number): IItemTraitSet {
    const result = cloneDeep(DefaultItemTraitSet)
    result.creatorIDs.push(loggedInUserID)
    result.statusType = StatusTypeValues.ACTIVE
    return result
  }
  public getItemTraitSet_DueNow(): IItemTraitSet {
    const result = cloneDeep(DefaultItemTraitSet)
    result.dueType = DueTypeValues.DUE_NOW
    result.statusType = StatusTypeValues.ACTIVE
    return result
  }
  public getItemTraitSet_Everything(): IItemTraitSet {
    return cloneDeep(DefaultItemTraitSet)
  }
  public getItemTraitSet_Inactive(): IItemTraitSet {
    const result = cloneDeep(DefaultItemTraitSet)
    result.statusType = StatusTypeValues.INACTIVE
    return result
  }
  public getItemTraitSet_Spam(): IItemTraitSet {
    const result = cloneDeep(DefaultItemTraitSet)
    result.isSpam = true
    return result
  }
  public getItemTraitSet_Unassigned(): IItemTraitSet {
    const result = cloneDeep(DefaultItemTraitSet)
    result.assigneeIDs.push(0)
    result.statusType = StatusTypeValues.ACTIVE
    return result
  }
  public getItemTraitSet_Watching(): IItemTraitSet {
    const result = cloneDeep(DefaultItemTraitSet)
    result.onlyWatching = true
    result.statusType = StatusTypeValues.ACTIVE
    return result
  }
}()
